import { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import SelectDropdownInput from '../../SelectDropdownInput/SelectDropdownInput'
import { fetchBuildingGuests } from '../../../reducers/guestsReducer'
import Icon from '../../Icons'

import ReservationNoteForm from './ReservationNoteForm'
import ReservationModifyPriceForm from './ReservationModifyPriceForm'

export const OccupantType = {
  Resident: 'Resident',
  Guest: 'Guest',
}

const mapGuests = ({ id, firstName, lastName, email, phone, last4 }) => ({
  id,
  userId: id,
  label: `${firstName} ${lastName}`,
  email: email,
  phone,
  last4,
})

const ReservationOccupantInfo = ({
  residents,
  buildingData,
  getClients,
  userData,
  guests,
  getBuildingGuests,
  onSelectOccupant,
  selectedOccupant,
  advancedSettings,
  onChangeAdvancedSettings,
  onAddNewGuest,
  occupantType,
  onChangeOccupantType,
  onUpdateCreditCardInformation,
  creditCardToken,
  residentCreditCardData,
  originalPrice,
  isFree,
  loadedAllClients,
}) => {
  const { t } = useTranslation(['reservations'])
  const currentUserId = userData.user ? userData.user.id : null
  const canAccessGuests = ['Administrator', 'PropertyManager'].includes(userData.user?.role)

  const fetchGuests = useCallback(
    async (skip, searchingQuery, isScrolling) => {
      const skipMore =
        guests?.length % 15 === 0
          ? guests?.length / 15
          : (guests?.length - (guests?.length % 15)) / 15
      if (typeof searchingQuery === 'undefined' || (searchingQuery === '' && isScrolling)) {
        try {
          await getBuildingGuests(
            typeof skip !== 'undefined' ? skip : skipMore + 1,
            buildingData.id,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else if (searchingQuery !== '' && isScrolling) {
        try {
          await getBuildingGuests(
            typeof skip !== 'undefined' ? skip : skipMore + 1,
            buildingData.id,
            searchingQuery,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else if (searchingQuery !== '' && !isScrolling) {
        try {
          await getBuildingGuests(
            typeof skip !== 'undefined' ? skip : searchingQuery ? 1 : skipMore + 1,
            buildingData.id,
            searchingQuery,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else {
        try {
          await getBuildingGuests(1, buildingData.id)
        } catch (err) {
          throw new Error(err)
        }
      }
    },
    [buildingData.id, guests],
  )

  const fetchResidents = useCallback(
    async (skip, searchingQuery, isScrolling) => {
      const skipMore =
        residents.length % 15 === 0
          ? residents.length / 15
          : (residents.length - (residents.length % 15)) / 15
      if (typeof searchingQuery === 'undefined' || (searchingQuery === '' && isScrolling)) {
        try {
          await getClients(
            typeof skip !== 'undefined' ? skip : skipMore + 1,
            buildingData.id,
            currentUserId,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else if (searchingQuery !== '' && isScrolling) {
        try {
          await getClients(
            typeof skip !== 'undefined' ? skip : skipMore + 1,
            buildingData.id,
            currentUserId,
            searchingQuery,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else if (searchingQuery !== '' && !isScrolling) {
        try {
          await getClients(
            typeof skip !== 'undefined' ? skip : searchingQuery ? 1 : skipMore + 1,
            buildingData.id,
            currentUserId,
            searchingQuery,
          )
        } catch (err) {
          throw new Error(err)
        }
      } else {
        try {
          await getClients(1, buildingData.id, currentUserId)
        } catch (err) {
          throw new Error(err)
        }
      }
    },
    [buildingData.id, residents, currentUserId],
  )

  useEffect(() => {
    fetchResidents(0, '', false)
    if (canAccessGuests) {
      fetchGuests(0, '', false)
    }
  }, [])

  const creditCardLast4 =
    selectedOccupant && selectedOccupant.type === OccupantType.Guest
      ? selectedOccupant.last4
      : selectedOccupant && residentCreditCardData
        ? residentCreditCardData.last4
        : ''

  const updatedCreditCardLast4 = creditCardToken && creditCardToken.card.last4

  const selectedValueLabel = selectedOccupant
    ? selectedOccupant.label ||
      `${selectedOccupant.firstName || ''} ${selectedOccupant.lastName || ''}`.trim()
    : ''

  const fetchData = useCallback(
    async (skip, query, isScrolling) => {
      if (occupantType === OccupantType.Resident) {
        await fetchResidents(skip, query, isScrolling)
      } else {
        await fetchGuests(skip, query, isScrolling)
      }
    },
    [occupantType, fetchResidents, fetchGuests],
  )

  return (
    <div className="reservation-occupant-info">
      <div className="reservation-occupant-info__occupant-type">
        <RadioField
          name="resident"
          data-testid="resident"
          label={t('reservations:reservationOccupantInfo.residentOption')}
          additionalClass="reservation-occupant-info__radio-button"
          checked={occupantType === OccupantType.Resident}
          onChange={() => {
            onSelectOccupant(undefined)
            onChangeOccupantType(OccupantType.Resident)
          }}
        />
        {canAccessGuests && (
          <RadioField
            name="guest"
            data-testid="guest"
            label={t('reservations:reservationOccupantInfo.guestOption')}
            additionalClass="reservation-occupant-info__radio-button"
            checked={occupantType === OccupantType.Guest}
            onChange={() => {
              onSelectOccupant(undefined)
              onChangeOccupantType(OccupantType.Guest)
            }}
          />
        )}
      </div>
      <div className="reservation-occupant-info__occupant-search">
        {occupantType === OccupantType.Guest
          ? t('reservations:reservationOccupantInfo.selectGuest')
          : t('reservations:reservationOccupantInfo.selectResident')}
        <SelectDropdownInput
          key={occupantType}
          placeholder={
            occupantType === OccupantType.Guest
              ? t('reservations:reservationOccupantInfo.searchGuestPlaceholder')
              : t('reservations:reservationOccupantInfo.searchResidentPlaceholder')
          }
          data={(occupantType === OccupantType.Resident ? residents : guests.map(mapGuests)) || []}
          selectedValueLabel={selectedValueLabel}
          onSelectOption={({ value, id, userId, creditCardId, label, last4 }) => {
            onSelectOccupant({
              label,
              clientId: value,
              userId,
              guestId: id,
              type: occupantType,
              creditCardId: occupantType === OccupantType.Resident ? creditCardId : !!last4,
              last4,
            })
          }}
          fetchData={fetchData}
          isLoadedAll={loadedAllClients}
        />
        <Icon
          icon={'search'}
          className="reservation-occupant-info__occupant-search-icon"
          fill="#3c3c3c"
        />
      </div>
      <div className="reservation-occupant-info__credit-card-info">
        {(creditCardLast4 || updatedCreditCardLast4) && !isFree && (
          <div className="reservation-occupant-info__credit-card-number">
            <span>
              {t('reservations:reservationOccupantInfo.billingCreditCard', {
                last4: updatedCreditCardLast4 || creditCardLast4,
              })}
            </span>
            <button
              onClick={() => onUpdateCreditCardInformation()}
              className="reservation-occupant-info__update-credit-card-button"
            >
              {t('reservations:reservationOccupantInfo.updateCreditCard')}
            </button>
          </div>
        )}
        {selectedOccupant &&
          !selectedOccupant.creditCardId &&
          !updatedCreditCardLast4 &&
          !isFree && (
            <div className="reservation-occupant-info__credit-card-warning">
              <div className="reservation-occupant-info__credit-card-warning-title">
                {t('reservations:reservationOccupantInfo.creditCardRequiredTitle')}
              </div>
              <p>{t('reservations:reservationOccupantInfo.creditCardRequiredDescription')}</p>
              <button onClick={() => onUpdateCreditCardInformation()}>
                {t('reservations:reservationOccupantInfo.addCreditCardInformation')}
              </button>
            </div>
          )}
        {occupantType === OccupantType.Guest && (
          <button
            onClick={() => onAddNewGuest()}
            className="reservation-occupant-info__add-guest-button"
          >
            {t('reservations:addReservationGuestForm.addNewGuest')}
          </button>
        )}
      </div>
      <div className="reservation-occupant-info__advanced-settings">
        <div className="reservation-occupant-info__advanced-settings-title">
          {t('reservations:reservationOccupantInfo.advancedSettings')}
        </div>
        <CheckboxField
          name={'note'}
          data-testid="note"
          label={t('reservations:reservationOccupantInfo.addReservationNote')}
          value={advancedSettings.note}
          onChange={() =>
            onChangeAdvancedSettings({
              note: !advancedSettings.note,
              noteTitle: '',
              noteBody: '',
            })
          }
        />
        {advancedSettings.note && (
          <ReservationNoteForm
            name={advancedSettings.noteTitle}
            description={advancedSettings.noteBody}
            onNameChange={(noteTitle) => onChangeAdvancedSettings({ noteTitle })}
            onDescriptionChange={(noteBody) => onChangeAdvancedSettings({ noteBody })}
          />
        )}
        <CheckboxField
          name={'price'}
          data-testid="price"
          label={t('reservations:reservationOccupantInfo.modifyPrice')}
          value={advancedSettings.modifyPrice}
          onChange={() => onChangeAdvancedSettings({ modifyPrice: !advancedSettings.modifyPrice })}
        />
        {advancedSettings.modifyPrice && (
          <ReservationModifyPriceForm
            originalPrice={originalPrice}
            type={advancedSettings.modifyPriceType}
            value={advancedSettings.modifyPriceValue}
            onChangeType={(modifyPriceType) => onChangeAdvancedSettings({ modifyPriceType })}
            onChangeValue={(modifyPriceValue) => onChangeAdvancedSettings({ modifyPriceValue })}
          />
        )}
        <CheckboxField
          name={'invoice'}
          data-testid="invoice"
          label={t('reservations:reservationOccupantInfo.sendInvoice')}
          value={advancedSettings.sendReceipt}
          onChange={() => onChangeAdvancedSettings({ sendReceipt: !advancedSettings.sendReceipt })}
        />
      </div>
    </div>
  )
}

ReservationOccupantInfo.propTypes = {
  occupantType: PropTypes.string,
  onChangeOccupantType: PropTypes.func,
  occupant: PropTypes.object,
  buildingData: PropTypes.object,
  getClients: PropTypes.func,
  userData: PropTypes.object,
  residents: PropTypes.array,
  guests: PropTypes.array,
  getBuildingGuests: PropTypes.func,
  onSelectOccupant: PropTypes.func,
  selectedOccupant: PropTypes.object,
  advancedSettings: PropTypes.object,
  onChangeAdvancedSettings: PropTypes.func,
  onAddNewGuest: PropTypes.func,
  onUpdateCreditCardInformation: PropTypes.func,
  creditCardToken: PropTypes.object,
  residentCreditCardData: PropTypes.object,
  originalPrice: PropTypes.number,
  isFree: PropTypes.bool,
  loadedAllClients: PropTypes.bool,
}

export default connect(null, { getBuildingGuests: fetchBuildingGuests })(ReservationOccupantInfo)
